import React, { useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardSection,
  CondensedLarge,
  Gutter,
  Paragraph,
  Progress,
  SmallHeader,
  useApiWithErrors,
  ApiError,
  PluralizeUtil,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';
import { Link, useNavigate } from 'react-router';

import { EnvUtil } from '../../utils/env-util';
import { WorkFeedApi } from '../../api/work-feed-api';

export const WorkFeedInterestedTracker = ({
  candidateId,
  name = 'WORK_FEED_INTERESTED_TRACKER',
}: {
  candidateId: Candidate['id'];
  name?: string;
}) => {
  const [interestedCountData] = useApiWithErrors(
    WorkFeedApi.readWorkFeedInterestedCount(candidateId)
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (interestedCountData instanceof ApiError) {
      const error = interestedCountData as ApiError;

      if (error.getStatusCode() === 403) {
        const failUrl = `../403`;
        void navigate(failUrl, { replace: true });
        return;
      }
      throw error;
    }
  }, [interestedCountData]);

  if (interestedCountData instanceof ApiError) return null;

  const { interestedCount } = interestedCountData;
  const trackerBarColor = (count: number) => {
    return count <= 6 ? 'green' : 'red';
  };

  const countMax = EnvUtil.workFeedMaxInterestedLimit;
  const remainingOpportunities =
    interestedCount > countMax ? 0 : countMax - interestedCount;

  return (
    <Card name="WORK_FEED_INTERESTED_COUNT">
      <CardHeader>
        <SmallHeader name="expression-tracker-info">
          Opportunity Interest Counter
        </SmallHeader>
      </CardHeader>
      <CardSection>
        <Progress
          currentStep={interestedCount}
          totalSteps={countMax}
          color={trackerBarColor(interestedCount)}
          name="INTERESTED_PROGRESS"
        />
        <CondensedLarge name="REMAINING_OPPORTUNITIES">
          You can express interest in <strong>{remainingOpportunities}</strong>{' '}
          more{' '}
          <PluralizeUtil
            text="opportunity"
            count={remainingOpportunities}
            options={{
              zero: 'opportunities.',
              one: 'opportunity.',
              many: 'opportunities.',
            }}
          />
        </CondensedLarge>
        <Gutter top="24px" />
        <Paragraph name="OPPORTUNITY_INFO">
          Axiom considers you for up to {countMax} opportunities at a time. To
          review roles you’re currently interested in, check{' '}
          <Link data-test="ENGAGEMENTS_LINK" to="../..">
            Engagements {'>'} Prospective Opportunities.
          </Link>
        </Paragraph>
      </CardSection>
    </Card>
  );
};
